import type {
  AssociatedPortService,
  MovementServiceDetailsBackendResponse,
} from "@features/vessel-call/vessel-call-status/MovementServices/preview/types";
import { api } from "@services/api";
import type {
  BerthAllocation,
  CloseMovementServicePayload,
  CompleteMovementPayload,
  Movement,
  MovementRequestPayload,
  MovementServicesApiResponse,
  MovementServicesFilterPayload,
  MovementUpdatePayload,
  PastMovementApiResponse,
} from "./movement-services/types";

interface ClaimMovementParams {
  service: string;
  id: string;
}

export const getMovements = async ({
  service,
  page = 0,
  pageSize = 25,
  filters,
}: {
  service: string;
  page?: number;
  pageSize?: number;
  filters: MovementServicesFilterPayload;
}) => {
  const data: MovementServicesApiResponse = await api.post(
    service,
    `/movement-services:search?pageSize=${pageSize}&pageNumber=${page}`,
    {
      ...filters,
    },
  );

  return {
    items: data.items,
    totalElements: data.totalElements,
  };
};

export const getCurrentMovementService = async ({
  service,
  vesselId,
  vesselCallId,
}: {
  service: string;
  vesselId: string;
  vesselCallId: string;
}) => {
  const data: Movement[] = await api.post(service, `movement-current-services:search`, {
    vesselId,
    vesselCallId,
  });

  return data;
};

export const getPastMovementServices = async ({
  service,
  page = 0,
  size = 25,
  vesselId,
  vesselCallId,
}: {
  service: string;
  page?: number;
  size?: number;
  vesselId?: string;
  vesselCallId: string;
}) => {
  const data: PastMovementApiResponse = await api.post(
    service,
    `movement-past-services:search?pageSize=${size}&pageNumber=${page}`,
    {
      vesselId,
      vesselCallId,
    },
  );

  return {
    items: data.items,
    totalElements: data.totalElements,
  };
};

export const getBerthAllocation = async ({
  service,
  fromTo,
}: {
  service: string;
  fromTo: string;
}) => {
  const data: BerthAllocation = await api.get(
    service,
    `movement-service-from-to?fromTo=${encodeURIComponent(fromTo)}`,
  );

  return data;
};

export const getMovementServiceDetailsById = async ({
  id,
  service,
}: {
  id: string;
  service: string;
}): Promise<MovementServiceDetailsBackendResponse> => {
  return api.get<MovementServiceDetailsBackendResponse>(service, `movement-service/${id}`);
};

export const submitMovementRequest = async ({
  service,
  payload,
  isResubmission,
  movementId: id,
  transactionId,
}: {
  service: string;
  payload: MovementRequestPayload;
  isResubmission?: boolean;
  movementId?: string;
  transactionId?: string;
}) => {
  const path = isResubmission ? `movement-service/${id}:resubmit` : "movement-service:start";
  return api.post(service, path, payload, ...(transactionId ? [{ transactionId }] : []));
};

export const claimMovementById = async ({
  service,
  id,
  transactionId,
}: ClaimMovementParams & { transactionId?: string }) => {
  return api.put(
    service,
    `movement-service/${id}:claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const unclaimMovementById = async ({
  service,
  id,
  transactionId,
}: ClaimMovementParams & { transactionId?: string }) => {
  return api.put(
    service,
    `movement-service/${id}:unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const completeMovementById = async ({
  id,
  service,
  payload,
  transactionId,
}: {
  id: string;
  service: string;
  payload: CompleteMovementPayload;
  transactionId?: string;
}): Promise<Movement> => {
  return api.put(
    service,
    `movement-service/${id}:complete`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const cancelMovementById = async ({
  id,
  service,
  payload,
  transactionId,
}: {
  id: string;
  service: string;
  payload: {
    reason: string;
    additionalDocument?: { url: string; type: string; originalFileName: string };
  };
  transactionId?: string;
}): Promise<Movement> => {
  return api.put(
    service,
    `movement-service/${id}:cancel`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updateMovementById = async ({
  id,
  service,
  payload,
  transactionId,
}: {
  id: string;
  service: string;
  payload: MovementUpdatePayload;
  transactionId?: string;
}): Promise<MovementServiceDetailsBackendResponse> => {
  return api.patch<MovementServiceDetailsBackendResponse>(
    service,
    `movement-service/${id}:update`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const closeMovementServiceById = async ({
  id,
  service,
  payload,
  transactionId,
}: {
  id: string;
  service: string;
  payload: CloseMovementServicePayload;
  transactionId?: string;
}): Promise<Movement> => {
  return api.patch(
    service,
    `movement-service/${id}:close`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const getAssociatedPortServiceDetailsById = async ({
  vesselCallId,
  movementReason,
  service,
}: {
  vesselCallId: string;
  movementReason: string;
  service: string;
}): Promise<AssociatedPortService[]> => {
  return api.get<AssociatedPortService[]>(
    service,
    `port-services/associated-movement-candidates?vesselCallId=${vesselCallId}&movementReason=${movementReason}`,
  );
};

export const addResource = async ({
  service,
  movementId,
  payload,
}: {
  service: string;
  movementId: string;
  payload: {
    additionalResourceType: string;
    numberOfResources: number;
    resourceName?: string | null;
    endTimeOfUsage?: string | null;
    startTimeOfUsage?: string | null;
    connectionTime?: string | null;
    location?: string | null;
  };
}): Promise<MovementServiceDetailsBackendResponse> => {
  return api.post(service, `movement-service/${movementId}/additional-resources`, payload);
};

export const updateResource = async ({
  service,
  movementId,
  resourceId,
  payload,
}: {
  service: string;
  movementId: string;
  resourceId: string;
  payload: {
    additionalResourceType: string;
    numberOfResources: number;
    resourceName?: string | null;
    endTimeOfUsage?: string | null;
    startTimeOfUsage?: string | null;
    connectionTime?: string | null;
    location?: string | null;
  };
}): Promise<MovementServiceDetailsBackendResponse> => {
  return api.put(
    service,
    `movement-service/${movementId}/additional-resources/${resourceId}`,
    payload,
  );
};

export const deleteResource = async ({
  service,
  movementId,
  resourceId,
}: {
  service: string;
  movementId: string;
  resourceId: string;
}): Promise<MovementServiceDetailsBackendResponse> => {
  return api.delete(service, `movement-service/${movementId}/additional-resources/${resourceId}`);
};
